// Live data for the AppShell (design-system spec §3.1): module counts from the
// modules' EXISTING list reads. There is no count endpoint and the lists are
// cursor-paginated, so ONE page is all a count slot can afford — which leaves
// the product with THREE genuinely different facts, not two:
//
//   exact    the whole list fit in one page; the number IS the count.
//   atLeast  the page came back WITH a cursor, so there are more rows than we
//            counted. "200+" is not an invented number — it is a true lower
//            bound, which is what the D-033 honesty rule actually demands.
//   unknown  the read failed, or has not answered yet. Nothing is claimed.
//
// This file used to collapse `atLeast` into `unknown`: one `exact()` helper
// returning `number | undefined`, absent whenever a cursor came back. So a
// tenant past 200 products got an em dash on the home KPI tile and NO number at
// all in the sidebar — permanently, and starting at exactly the size where a
// count begins to be worth reading. (Owner finding on deployed staging,
// 2026-08-20.) The old header's argument — "a tenant big enough to overflow the
// page gets NO count rather than a fake one" — was right about not inventing
// numbers and wrong about the RENDERING: a dash reads as "broken" or "zero",
// and both of those are false statements about a factory with 3000 SKUs.
//
// The lower bound is honest at the page boundary too. `next_cursor` is emitted
// whenever `items.length === limit` (apps/api catalogue-reads.service.ts), so a
// tenant holding EXACTLY 200 products also gets a cursor; "200+" read as "at
// least 200" is still true there, which is why the form is a bound and not
// "more than 200".
//
// The nav row reserves its space either way, so a count resolving after first
// paint never moves the chrome (§5).

import { useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { client } from "../lib/api";

const COUNT_STALE_MS = 60_000;
/** One page at the server maximum — both list contracts cap `limit` at 200. */
const COUNT_PAGE_LIMIT = 200;

/**
 * What a count slot actually knows. Three states because the product has three;
 * the type exists so that collapsing any two of them into one glyph has to be a
 * deliberate act at a call site rather than a silent property of the data layer.
 */
export type ShellCount =
  | { readonly kind: "exact"; readonly n: number }
  | { readonly kind: "atLeast"; readonly n: number }
  | { readonly kind: "unknown" };

/** "We could not read this." Shared so a render never allocates a fresh one. */
export const COUNT_UNKNOWN: ShellCount = { kind: "unknown" };

/**
 * One page of a CURSOR-PAGINATED list → the count state. Pure.
 *
 * The bound is the page's own length, not `COUNT_PAGE_LIMIT`: a server that
 * answers a short page and still hands back a cursor is telling the truth about
 * "there are more", and `items.length` is the largest number we have actually
 * counted. Deriving it from the constant would be a guess about the server.
 */
export function countFromPage(
  items: readonly unknown[] | undefined,
  nextCursor: string | null | undefined,
): ShellCount {
  if (items === undefined) return COUNT_UNKNOWN;
  if (nextCursor === null || nextCursor === undefined) return { kind: "exact", n: items.length };
  return { kind: "atLeast", n: items.length };
}

/**
 * A list read that is NOT paginated at all — `GET /v1/org/work-centers` returns
 * every row (`WorkCenterListResponse` is `{ items }`, no cursor, and the repo
 * query carries no LIMIT) — so the array IS the population and only two states
 * exist. Kept separate from `countFromPage` so "this one cannot overflow" is a
 * claim made once, here, next to the contract that justifies it, instead of
 * being re-derived at a tile that cannot see the contract.
 */
export function countOfWholeList(items: readonly unknown[] | undefined): ShellCount {
  return items === undefined ? COUNT_UNKNOWN : { kind: "exact", n: items.length };
}

/**
 * The compact label a count slot renders: `"37"`, `"200+"`, or `undefined` when
 * nothing is known. Pure — `t` is passed in — so all three states are unit
 * testable without a React tree.
 *
 * `undefined` rather than a dash, deliberately: the two surfaces say "unknown"
 * differently and only the caller knows which is right for its own geometry.
 * The home KPI tile paints an em dash (`count.unknown`), because a tile with an
 * empty value slot reads as a broken tile; the sidebar paints NOTHING, because
 * the chrome never reports a read failure and the row looks the same with or
 * without a number (§5).
 */
export function shellCountLabel(count: ShellCount, t: TFunction): string | undefined {
  if (count.kind === "unknown") return undefined;
  // Not `toLocaleString`: a grouping separator would change the digits a
  // walkthrough asserts, and these are nav-width slots, not report figures.
  if (count.kind === "exact") return String(count.n);
  return t("count.atLeast", { count: count.n });
}

/** A shell count read with its settle state — the home gates its atomic reveal
 * (§5 zero-CLS on load) on `isSettled`, which is true once the read has
 * fetched OR failed (fail-closed absence, never an eternal skeleton). */
export interface ShellCountRead {
  count: ShellCount;
  isSettled: boolean;
  /** `dataUpdatedAt` of the underlying query (0 until first success). */
  updatedAt: number;
}

/** Catalogue products count (sidebar "Catalogue" row), with settle state. */
export function useCatalogueCountRead(enabled: boolean): ShellCountRead {
  const q = useQuery({
    queryKey: ["shell", "catalogue-count"],
    enabled,
    retry: false,
    staleTime: COUNT_STALE_MS,
    queryFn: async ({ signal }) => {
      const { data } = await client.GET("/v1/catalogue/products", {
        signal,
        params: { query: { limit: COUNT_PAGE_LIMIT } },
      });
      // Absence on any failure — the shell never errors over a count.
      return data ?? null;
    },
  });
  const body = q.data ?? undefined;
  return {
    count: countFromPage(body?.items, body?.next_cursor),
    isSettled: q.isFetched,
    updatedAt: q.dataUpdatedAt,
  };
}

/** Open WOs (released + in progress) for "Production", with settle state. */
export function useOpenWorkOrderCountRead(enabled: boolean): ShellCountRead {
  const q = useQuery({
    queryKey: ["shell", "open-wo-count"],
    enabled,
    retry: false,
    staleTime: COUNT_STALE_MS,
    queryFn: async ({ signal }) => {
      const { data } = await client.GET("/v1/work-orders", {
        signal,
        params: { query: { status: ["released", "in_progress"], limit: COUNT_PAGE_LIMIT } },
      });
      return data ?? null;
    },
  });
  const body = q.data ?? undefined;
  return {
    count: countFromPage(body?.items, body?.next_cursor),
    isSettled: q.isFetched,
    updatedAt: q.dataUpdatedAt,
  };
}

/** Open work orders (released + in progress) for the "Production" row. */
export function useOpenWorkOrderCount(enabled: boolean): ShellCount {
  return useOpenWorkOrderCountRead(enabled).count;
}

/** Environment chip — derived from the host, which cannot be stale. The label
 * itself is an i18n key (`nav.envStaging`); this only decides presence. */
export function isStagingHost(hostname: string): boolean {
  return hostname.includes(".staging.") || hostname === "localhost" || hostname.startsWith("127.");
}

/** Tenant display label — the tenant's own subdomain, known synchronously so
 * the topbar paints complete on the first frame (§5 render-once). */
export function tenantLabel(hostname: string): string | undefined {
  const first = hostname.split(".")[0];
  if (first === undefined || first.length === 0 || first === "localhost") return undefined;
  return first;
}
